import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, ChevronRight, Award } from "lucide-react";
import { useDashboardData } from "../../hooks/useDashboardData";
import { Sparkline, SectionHeader } from "../components/Shared";

const CEFR_LABELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const CEFR_MAP = { A1: 0, A2: 1, B1: 2, B2: 3, C1: 4, C2: 5 };

// ─── Skeleton helper ──────────────────────────────────────────────────────────

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-panel/10 ${className}`} />
);

// ─── CEFR Dial ────────────────────────────────────────────────────────────────

const CEFRDial = ({ cefrLabel }) => {
  const numericValue = CEFR_MAP[cefrLabel] ?? 0;
  const fraction = numericValue / 5;
  const size = 220, stroke = 14, R = (size - stroke) / 2;
  const circ = 2 * Math.PI * R;
  const [animated, setAnimated] = useState(0);

  useEffect(() => {
    let raf, start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / 1300);
      setAnimated(p * fraction);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fraction]);

  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id="dialGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="rgb(var(--accent-cyan-rgb))" />
            <stop offset="100%" stopColor="rgb(var(--accent-violet-rgb))" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke="rgb(var(--panel-rgb) / 0.08)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke="url(#dialGrad)" strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - animated)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ filter: "drop-shadow(0 0 10px rgb(var(--accent-cyan-rgb) / 0.45))" }}
        />
      </svg>
      <div className="absolute text-center">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">CEFR · live</div>
        <div className="font-display text-[44px] font-light tracking-tight text-ink leading-none mt-1">{cefrLabel}</div>
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-aura-cyan mt-1 flex items-center gap-1 justify-center">
          <TrendingUp size={11} /> {numericValue > 0 ? "In progress" : "Just starting"}
        </div>
      </div>
    </div>
  );
};

// ─── Heatmap from session_logs ────────────────────────────────────────────────

const Heatmap = ({ logs }) => {
  // Build a set of dates → intensity 0-4
  const dateMap = {};
  logs.forEach((l) => {
    const d = l.created_at.slice(0, 10);
    dateMap[d] = Math.min(4, (dateMap[d] || 0) + 1);
  });

  const cols = 12, rows = 7;
  const cells = [];
  const today = new Date();
  // Start from 12 weeks ago (Monday)
  const start = new Date(today);
  start.setDate(today.getDate() - (cols * rows - 1));

  for (let i = 0; i < cols * rows; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const key = d.toISOString().slice(0, 10);
    cells.push(dateMap[key] || 0);
  }

  return (
    <div className="overflow-x-auto" data-testid="progress-heatmap">
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(14px, 1fr))`, gridAutoRows: "14px" }}>
        {Array.from({ length: cols * rows }).map((_, i) => {
          const col = i % cols, row = Math.floor(i / cols);
          const v = cells[col * rows + row] ?? 0;
          const a = [0.05, 0.18, 0.35, 0.6, 0.92][v];
          return <div key={i} className="rounded-[3px]" style={{ background: `rgb(var(--accent-cyan-rgb) / ${a})` }} />;
        })}
      </div>
      <div className="mt-2 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
        <span>less</span>
        {[0, 1, 2, 3, 4].map((v) => (
          <span key={v} className="w-3 h-3 rounded-[3px]" style={{ background: `rgb(var(--accent-cyan-rgb) / ${[0.05, 0.18, 0.35, 0.6, 0.92][v]})` }} />
        ))}
        <span>more</span>
      </div>
    </div>
  );
};

// ─── CEFR Trajectory from session history ─────────────────────────────────────

const TrajChart = ({ logs, currentCefr }) => {
  const cefrNumeric = CEFR_MAP[currentCefr] ?? 0;
  // Build data points: first session = 0.0, current = cefrNumeric, interpolated
  const pts12 = Array.from({ length: 12 }, (_, i) => {
    const t = i / 11;
    // Smooth curve from 0 to current
    return Math.min(cefrNumeric, t * cefrNumeric + Math.sin(t * Math.PI * 0.5) * 0.3);
  });

  const data = pts12;
  const w = 600, h = 140, pad = 14;
  const min = 0, max = 5;
  const pts = data.map((d, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = h - pad - ((d - min) / (max - min)) * (h - pad * 2);
    return [x, y];
  });
  const path = pts.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(" ");
  const area = `${path} L ${pts[pts.length - 1][0]},${h - pad} L ${pts[0][0]},${h - pad} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" preserveAspectRatio="none">
      <defs>
        <linearGradient id="trajFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgb(var(--accent-cyan-rgb) / 0.35)" />
          <stop offset="100%" stopColor="rgb(var(--accent-cyan-rgb) / 0)" />
        </linearGradient>
      </defs>
      {[1, 2, 3, 4].map((v) => {
        const y = h - pad - (v / 5) * (h - pad * 2);
        return <line key={v} x1={pad} x2={w - pad} y1={y} y2={y} stroke="rgb(var(--panel-rgb) / 0.06)" strokeDasharray="2 4" />;
      })}
      <path d={area} fill="url(#trajFill)" />
      <path d={path} fill="none" stroke="rgb(var(--accent-cyan-rgb))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 8px rgb(var(--accent-cyan-rgb) / 0.6))" }} />
      {pts.map((p, i) => (<circle key={i} cx={p[0]} cy={p[1]} r={i === pts.length - 1 ? 4 : 2} fill="rgb(var(--accent-cyan-rgb))" />))}
    </svg>
  );
};

// ─── Signals from memory_json ─────────────────────────────────────────────────

function buildSignals(memory, stats) {
  return [
    {
      key: "vocab_mastered",
      label: "Vocab Mastered",
      value: memory.mastered_vocabulary?.length || 0,
      delta: memory.mastered_vocabulary?.length > 0 ? `+${memory.mastered_vocabulary.length}` : "none",
      tone: memory.mastered_vocabulary?.length > 0 ? "up" : "neutral",
      history: Array.from({ length: 10 }, (_, i) => Math.round((memory.mastered_vocabulary?.length || 0) * (i / 9))),
    },
    {
      key: "vocab_gaps",
      label: "Vocab Gaps",
      value: memory.failed_vocabulary?.length || 0,
      delta: memory.failed_vocabulary?.length > 0 ? `${memory.failed_vocabulary.length} flagged` : "clean",
      tone: memory.failed_vocabulary?.length > 0 ? "down" : "up",
      history: Array.from({ length: 10 }, (_, i) => Math.round((memory.failed_vocabulary?.length || 0) * (i / 9))),
    },
    {
      key: "strengths",
      label: "Strengths",
      value: memory.strengths?.length || 0,
      delta: memory.strengths?.length > 0 ? `${memory.strengths.length} noted` : "none",
      tone: memory.strengths?.length > 0 ? "up" : "neutral",
      history: Array.from({ length: 10 }, (_, i) => Math.round((memory.strengths?.length || 0) * (i / 9))),
    },
    {
      key: "sessions",
      label: "Sessions",
      value: stats.logs.length,
      delta: stats.logs.length > 0 ? `${stats.logs.length} total` : "none yet",
      tone: stats.logs.length > 0 ? "up" : "neutral",
      history: Array.from({ length: 10 }, (_, i) => Math.round(stats.logs.length * (i / 9))),
    },
    {
      key: "streak",
      label: "Streak",
      value: stats.streak,
      delta: stats.streak > 0 ? `${stats.streak} days` : "start today",
      tone: stats.streak > 0 ? "up" : "neutral",
      history: Array.from({ length: 10 }, (_, i) => Math.round(stats.streak * (i / 9))),
    },
    {
      key: "context",
      label: "Personal Context",
      value: memory.personal_context?.length || 0,
      delta: memory.personal_context?.length > 0 ? `${memory.personal_context.length} facts` : "none yet",
      tone: "neutral",
      history: Array.from({ length: 10 }, (_, i) => Math.round((memory.personal_context?.length || 0) * (i / 9))),
    },
  ];
}

// ─── Achievements from milestones ─────────────────────────────────────────────

function buildAchievements(memory, stats) {
  const achievements = [];
  if (stats.streak >= 3) achievements.push({ id: "streak3", label: `${stats.streak}-day streak`, date: "Active" });
  if (memory.mastered_vocabulary?.length >= 5) achievements.push({ id: "vocab5", label: "5 vocab words mastered", date: "Milestone" });
  if (memory.mastered_vocabulary?.length >= 10) achievements.push({ id: "vocab10", label: "10 vocab words mastered", date: "Milestone" });
  if (stats.logs.length >= 1) achievements.push({ id: "first", label: "First session completed", date: stats.logs[stats.logs.length - 1]?.created_at?.slice(0, 10) || "Achieved" });
  if (memory.strengths?.length >= 3) achievements.push({ id: "strengths", label: `${memory.strengths.length} strengths identified`, date: "Ongoing" });

  // Fallback for new users
  if (achievements.length === 0) {
    achievements.push({ id: "starting", label: "Welcome to AURA — start your journey", date: "Today" });
  }
  return achievements.slice(0, 5);
}

// ─── Progress page ────────────────────────────────────────────────────────────

const Progress = () => {
  const { memory, sessionStats, isLoading, isNewUser } = useDashboardData();

  const cefrLabel = memory.cefr_level || "A1";
  const signals = buildSignals(memory, sessionStats);
  const achievements = buildAchievements(memory, sessionStats);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="page-progress">
      {/* Hero with dial + trajectory */}
      <div className="grid lg:grid-cols-12 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="lg:col-span-4 rounded-3xl glass edge-glow p-6 grid place-items-center"
          data-testid="progress-dial-card"
        >
          {isLoading ? (
            <Skeleton className="w-[220px] h-[220px] rounded-full" />
          ) : (
            <CEFRDial cefrLabel={cefrLabel} />
          )}
          <div className="mt-4 grid grid-cols-6 gap-1 w-full">
            {CEFR_LABELS.map((l, i) => {
              const reached = i <= (CEFR_MAP[cefrLabel] ?? 0);
              return (
                <div key={l} className={`text-center py-1 rounded font-mono text-[10px] tracking-[0.18em] uppercase ${reached ? "text-ink bg-aura-cyan/10" : "text-whisper-muted"}`}>{l}</div>
              );
            })}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
          className="lg:col-span-8 rounded-3xl glass hairline p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="section-label">CEFR trajectory · 12 weeks</div>
              <div className="mt-1 font-display text-[20px] font-light text-ink">
                {isNewUser ? (
                  <span className="text-whisper">Starts when you begin</span>
                ) : (
                  <>Starting <em>→ {cefrLabel}</em></>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4">
            {isLoading ? <Skeleton className="h-36 rounded-xl" /> : <TrajChart logs={sessionStats.logs} currentCefr={cefrLabel} />}
          </div>
          <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-whisper-muted">
            {["W1", "W3", "W6", "W9", "W12"].map((l) => <span key={l}>{l}</span>)}
          </div>
        </motion.div>
      </div>

      {/* Signals */}
      <div>
        <SectionHeader eyebrow="Signals" title="Six things she's listening to." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="progress-signals">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-36 rounded-2xl" />)
          ) : (
            signals.map((s) => (
              <motion.div key={s.key} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
                className="glass rounded-2xl p-5 hairline"
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{s.label}</div>
                  <div className={`font-mono text-[10.5px] tracking-[0.16em] uppercase ${s.tone === "up" ? "text-aura-cyan" : s.tone === "down" ? "text-red-300" : "text-whisper-muted"}`}>{s.delta}</div>
                </div>
                <div className="mt-3 flex items-end gap-3">
                  <div className="font-display text-[34px] font-light leading-none tabular-nums text-ink">{s.value}</div>
                  <div className="flex-1 h-1.5 rounded-full bg-panel/5 overflow-hidden mb-2">
                    <div className="h-full rounded-full bg-gradient-to-r from-aura-cyan to-aura-blue" style={{ width: `${Math.min(100, s.value * 10)}%` }} />
                  </div>
                </div>
                <div className="mt-3 h-9"><Sparkline data={s.history} height={36} /></div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Heatmap + Achievements */}
      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 glass rounded-3xl p-6 hairline">
          <div className="flex items-center justify-between">
            <div>
              <div className="section-label">Practice calendar · 12 weeks</div>
              <div className="mt-1 font-display text-[20px] font-light text-ink">
                {sessionStats.streak > 0 ? `${sessionStats.streak}-day streak` : "No streak yet"} · {sessionStats.minutesThisWeek}m this week
              </div>
            </div>
            <span className="pill">{sessionStats.streak > 2 ? "consistent" : isNewUser ? "starting" : "building"}</span>
          </div>
          <div className="mt-4">
            {isLoading ? <Skeleton className="h-28 rounded-xl" /> : <Heatmap logs={sessionStats.logs} />}
          </div>
        </div>

        <div className="lg:col-span-5 glass rounded-3xl p-6 hairline" data-testid="progress-achievements">
          <div className="section-label">Breakthroughs</div>
          {isLoading ? (
            <div className="mt-3 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {achievements.map((a) => (
                <div key={a.id} className="flex items-center gap-3 hairline rounded-xl p-3">
                  <span className="w-9 h-9 rounded-lg grid place-items-center bg-aura-cyan/10 border border-aura-cyan/20 text-aura-cyan">
                    <Award size={15} />
                  </span>
                  <div className="flex-1">
                    <div className="font-display text-[14.5px] text-ink leading-tight">{a.label}</div>
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mt-0.5">{a.date}</div>
                  </div>
                  <ChevronRight size={14} className="text-whisper-muted" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Progress;
